<template>
  <div class="ma-4">
    <v-snackbar v-model="msg" top>
      {{ text }}
      <v-btn color="pink" text @click="msg = false">Close</v-btn>
    </v-snackbar>

    <v-card>
      <v-layout row wrap class="pa-2 ma-2">
        <v-flex xs12>
          <h3 class="text-center mb-5">Training History</h3>
          <v-divider class="ml-4 mr-4 mb-3"></v-divider>
        </v-flex>

        <v-flex xs12>
          <v-card class="ma-3 pa-3" elevation="0">
            <v-layout row wrap>
              <v-flex xs12 md3 class="pa-1">
                <v-text-field
                  v-model="search"
                  label="Search"
                  prepend-icon="mdi-magnify"
                  single-line
                  hide-details
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md3 class="pa-1">
                <DatePicker
                  :default-date="filters.startDate"
                  v-model="filters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                />
              </v-flex>

              <v-flex xs12 md3 class="pa-1">
                <DatePicker
                  :default-date="filters.endDate"
                  v-model="filters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                />
              </v-flex>

              <v-flex xs12 md3 class="pa-1">
                <v-btn color="primary" @click="fetchTrainingHistory">Apply Filters</v-btn>
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>

        <v-flex xs12 class="pa-2">
          <v-data-table
            :headers="headers"
            :items="trainings"
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalItems"
            :page="page"
            :pageCount="totalPages"
            class="elevation-1"
          >
            <template v-slot:item.attendees="{ item }">
              <v-chip
                v-for="attendee in item.attendees"
                :key="attendee.employeeName"
                class="ma-1"
                small
              >
                {{ attendee.employeeName }}
              </v-chip>
            </template>

            <template v-slot:item.documents="{ item }">
              <v-chip
                v-for="doc in item.Document"
                :key="doc._id"
                class="ma-1"
                small
                color="primary"
                text-color="white"
              >
                {{ doc.Name }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                small
                color="primary"
                @click="viewTrainingDetails(item)"
              >
                View Details
              </v-btn>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Training Details Dialog -->
    <v-dialog v-model="dialog" max-width="900px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Training Details</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-list>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Training Name</v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">{{ selectedTraining.Training }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Category</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedTraining.Category }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Description</v-list-item-title>
                      <v-list-item-subtitle class="text-wrap" style="white-space: pre-wrap;">{{ selectedTraining.Description }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12">
                <h3 class="mb-4">Attendees</h3>
                <v-data-table
                  :headers="attendeeHeaders"
                  :items="selectedTraining.attendees || []"
                  class="elevation-1"
                  :items-per-page="5"
                >
                  <template v-slot:item.date="{ item }">
                    {{ item.date | toLocalDate }}
                  </template>
                  <template v-slot:item.timeStarted="{ item }">
                    {{ formatTime(item.timeStarted) }}
                  </template>
                  <template v-slot:item.timeEnded="{ item }">
                    {{ formatTime(item.timeEnded) }}
                  </template>
                  <template v-slot:item.duration="{ item }">
                    {{ item.duration }}
                  </template>
                  <template v-slot:item.signed="{ item }">
                    <v-icon
                      :color="item.signed ? 'green' : 'red'"
                      small
                    >
                      {{ item.signed ? 'mdi-check' : 'mdi-close' }}
                    </v-icon>
                  </template>
                  <template v-slot:item.documents="{ item }">
                    <v-chip
                      v-for="doc in item.documents"
                      :key="doc._id"
                      class="ma-1"
                      small
                      color="primary"
                      text-color="white"
                      @click="openDocument(doc.AWSLink)"
                      style="cursor: pointer"
                    >
                      {{ doc.Name }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-col>

              <v-col cols="12">
                <h3>Global Training Documents</h3>
                <v-row v-if="selectedTraining.Document && selectedTraining.Document.length > 0">
                  <v-col cols="12" sm="6" md="4" v-for="doc in selectedTraining.Document" :key="doc._id">
                    <v-card>
                      <v-card-text>
                        <template v-if="isPDF(doc.AWSLink)">
                          <LoadPDF :pdfFile="doc.AWSLink" :pdfName="doc.Name" />
                        </template>
                        <template v-else-if="isImage(doc.AWSLink)">
                          <v-img :src="doc.AWSLink" max-height="200" contain></v-img>
                        </template>
                        <template v-else-if="isDoc(doc.AWSLink)">
                          <v-btn color="primary" :href="doc.AWSLink" target="_blank" download>
                            <v-icon left>mdi-file-document</v-icon>
                            {{ doc.Name || 'Download Document' }}
                          </v-btn>
                        </template>
                        <template v-else>
                          <v-btn color="primary" :href="doc.AWSLink" target="_blank">
                            <v-icon left>mdi-file-document</v-icon>
                            {{ doc.Name || 'View Document' }}
                          </v-btn>
                        </template>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                <div v-else class="text-center pa-4">
                  No global training documents available
                </div>
              </v-col>

              <v-col cols="12">
                <h3>Inservice Training Documents</h3>
                <v-row v-if="selectedTraining.attendees && selectedTraining.attendees.length > 0">
                  <v-col cols="12" sm="6" md="4" v-for="attendee in selectedTraining.attendees" :key="attendee.employeeName">
                    <v-card v-if="attendee.documents && attendee.documents.length > 0">
                      <v-card-title>{{ attendee.employeeName }}</v-card-title>
                      <v-card-text>
                        <div v-for="doc in attendee.documents" :key="doc._id">
                          <template v-if="isPDF(doc.AWSLink)">
                            <LoadPDF :pdfFile="doc.AWSLink" :pdfName="doc.Name" />
                          </template>
                          <template v-else-if="isImage(doc.AWSLink)">
                            <v-img :src="doc.AWSLink" max-height="200" contain></v-img>
                          </template>
                          <template v-else-if="isDoc(doc.AWSLink)">
                            <v-btn color="primary" :href="doc.AWSLink" target="_blank" download>
                              <v-icon left>mdi-file-document</v-icon>
                              {{ doc.Name || 'Download Document' }}
                            </v-btn>
                          </template>
                          <template v-else>
                            <v-btn color="primary" :href="doc.AWSLink" target="_blank">
                              <v-icon left>mdi-file-document</v-icon>
                              {{ doc.Name || 'View Document' }}
                            </v-btn>
                          </template>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                <div v-else class="text-center pa-4">
                  No inservice training documents available
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import LoadPDF from '@/components/LoadPDF.vue';
const url = require("../client_config/config").url;
import DatePicker from "@/components/DateTimePickers/DatePicker.vue";
export default {
  name: 'TrainingHistory',
  components: {
    DatePicker,
    LoadPDF
  },
  data: () => ({
    msg: false,
    text: '',
    loading: false,
    search: '',
    dialog: false,
    selectedTraining: {},
    page: 1,
    totalPages: 0,
    totalItems: 0,
    options: {},
    filters: {
      startDate: '',
      endDate: '',
      company: ''
    },
    headers: [
      { text: 'Training Name', value: 'Training' },
      { text: 'Category', value: 'Category' },
      { text: 'Description', value: 'Description' },
      { text: 'Attendees', value: 'attendees', sortable: false },
      { text: 'Documents', value: 'documents', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false }
    ],
    attendeeHeaders: [
      { text: 'Employee Name', value: 'employeeName' },
      { text: 'Date', value: 'date' },
      { text: 'Time Started', value: 'timeStarted' },
      { text: 'Time Ended', value: 'timeEnded' },
      { text: 'Duration (hrs)', value: 'duration' },
      { text: 'Signed', value: 'signed' },
      { text: 'Documents', value: 'documents', sortable: false }
    ],
    trainings: []
  }),

  watch: {
    options: {
      handler() {
        this.fetchTrainingHistory();
      },
      deep: true
    }
  },

  created() {
    this.fetchTrainingHistory();
  },

  methods: {
    async fetchTrainingHistory() {
      try {
        this.loading = true;
        let startDate = null;
        let endDate = null;

        if(this.filters.startDate) {
        startDate = new Date(this.filters.startDate);
        startDate.setHours(0, 0, 0, 0);
        }


        if(this.filters.endDate) {
          endDate = new Date(this.filters.endDate);
          endDate.setHours(23, 59, 59, 999);
        }

        const params = {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          page: this.options.page,
          limit: this.options.itemsPerPage
        };

        const response = await axios.get(`${url}/globalTraining/complete-history`, { params });
        this.trainings = response.data.docs;
        this.totalItems = response.data.totalDocs;
        this.totalPages = response.data.totalPages;
        this.page = response.data.page;
      } catch (error) {
        console.error('Error fetching training history:', error);
        this.msg = true;
        this.text = 'Error fetching training history';
      } finally {
        this.loading = false;
      }
    },

    formatTime(timeValue) {
      if (!timeValue) return '';

      if (timeValue.includes('AM') || timeValue.includes('PM')) {
        return timeValue;
      }

      if (timeValue.includes('T')) {
        const date = new Date(timeValue);
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';

        if (hours === 0) {
          hours = 12;
        } else if (hours > 12) {
          hours = hours % 12;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}${ampm}`;
      }

      return timeValue;
    },

    viewTrainingDetails(training) {
      this.selectedTraining = training;
      this.dialog = true;
    },

    getFileType(file) {
      if (!file) return '';
      const extension = file.split('.').pop().toLowerCase();
      return extension;
    },

    isPDF(file) {
      return this.getFileType(file) === 'pdf';
    },

    isImage(file) {
      return ['jpg', 'jpeg', 'png'].includes(this.getFileType(file));
    },

    isDoc(file) {
      return ['doc', 'docx'].includes(this.getFileType(file));
    }
  }
};
</script>
